/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {isUndefined, trim, uniq} from 'lodash';
import isEqual from 'lodash.isequal';
import isObject from 'lodash.isobject';
import moment from 'moment';
import {getAccountKey, updateCheckboxTreeState} from '@ion/core/src/entities/Accounts';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {CheckboxState} from '@ion/core/src/types/general';
import {removeDuplicate} from '@ion/core/src/utils/ObjectUtil';
import Chip from '@ion/dls/src/components/chip/Chip';
import {
  resolveFilter
} from '@ion/dls/src/components/filters/FilterUtils';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {AllFilterTypes, DateSelectorValues, Filters, IconSize, TrackingFilters} from '@ion/dls/src/types/types';
import {
  SelectedFiltersProps
} from '@/components/filter/types';
import {getItemAllowed} from '../../store/actions/helpers';
import {
  BreadcrumbClickParams,
  displayFilterChildrenParams,
  FiltersHandleChangeParms,
  FiltersOnApplyParams,
  NavigateListParams,
  ProcessedFilters,
  SelectAllParams
} from './types';

/**
 *
 * @function getSelectedFilterHeader
 */
export const getSelectedFilterHeader = (
  multiSelect: boolean,
  showCount: boolean,
  selectedVal: Array<string | object>
) => {
  let floatingLabel   = '';
  const count         = selectedVal.length;
  const hasSelections = count > 0;
  if (multiSelect) {
    if (showCount) {
      floatingLabel = ` ${hasSelections ? ` (${count})` : ''}`;
    } else {
      floatingLabel = ` ${hasSelections ? ` ${selectedVal.join(',')}` : ''}`;
    }
  } else {
    floatingLabel = ` ${hasSelections ? ` ${resolveFilter(selectedVal[0])?.label}` : ''}`;
  }
  return floatingLabel;
};

/**
 *
 * @function processDateRange
 */
export const processDateRange = (range) => {
  let dateRange = null;
  if (Object.keys(range).length === 2) {
    const rangeVal = Object.values(range);
    const fromDate = moment(rangeVal[0]).format('DD.MM.YYYY').toString();
    const toDate   = moment(rangeVal[1]).format('DD.MM.YYYY').toString();
    dateRange      = `${fromDate} - ${toDate}`;
  }
  return dateRange;
};

/**
 *
 * @function onApply
 */
export const onApply = ({
  isGroupsFilter,
  accounts,
  isDateFilter,
  showDateRangePicker,
  activeIndex,
  selectedVal,
  selectedItems,
  dateRange,
  setSelectedFilter,
  toggleDetailView,
  setSelectedVal,
  setShowDateRangePicker,
  closeGeofenceFilter
}: FiltersOnApplyParams) => () => {
  if (isGroupsFilter) {
    setSelectedFilter(selectedVal, accounts);
    toggleDetailView(activeIndex);
  } else if (showDateRangePicker) {
    const processedRange = processDateRange(dateRange);
    if (processedRange === null) {
      const updates = [selectedItems];
      setSelectedVal(updates);
    } else {
      const key = Object.keys(selectedVal[0])[0];
      const val = {[key]: [processedRange]};
      const updates = [val];
      setSelectedVal(updates);
    }
    setShowDateRangePicker(false);
  } else {
    toggleDetailView(activeIndex);
    setSelectedFilter(selectedVal);
  }
  closeGeofenceFilter?.();
};

/**
 *
 * @function resetGroupFilters
 */
export const resetGroupFilters = (toggle, accounts) => {
  let updatedTree;
  if (accounts) {
    const currentTree = Object.keys(accounts).filter(key => !accounts[key]?.parentId);
    currentTree?.forEach(accountId => {
      updatedTree = updateCheckboxTreeState(accountId, toggle
        ? CheckboxState.Checked : CheckboxState.Unchecked, updatedTree || accounts);
    });
  }
  return updatedTree;
};

/**
 *
 * @function toggleSelectAll
 */
export const toggleSelectAll = ({
  toggle,
  options,
  setSelectedVal,
  isGroupsFilter,
  accounts,
  setAccounts
}: SelectAllParams) => {
  if (isGroupsFilter) {
    setAccounts(resetGroupFilters(toggle, accounts));
  } else {
    const updates = [...options];
    setSelectedVal(toggle ? updates : []);
  }
};

/**
 *
 * @function handleFilterClick
 */
export const handleFilterClick = (
  index: number,
  toggleDetailView: Function,
  setActiveFilterIndex: Function
) => {
  setActiveFilterIndex(index);
  toggleDetailView(index);
};

/**
 *
 * @function handleChipClick
 */
export const handleChipClick = (
  option: string | object,
  selectedVal: Array<string | object>,
  setSelectedVal: Function
) => {
  const {value} = resolveFilter(option);
  const updates = selectedVal.filter(selection =>
    resolveFilter(selection)?.value !== value);
  setSelectedVal(updates);
};

/**
 *
 * @function navigateList
 */
export const navigateList = ({
  title,
  subAccounts,
  setNestedSelection,
  groupBreadCrumbs,
  setGroupBreadCrumbs
}: NavigateListParams) => {
  const crumbs  = {subAccounts, title};
  const updates = [...groupBreadCrumbs, crumbs];
  setNestedSelection(subAccounts);
  setGroupBreadCrumbs(updates);
};

/**
 *
 * @function handleCrumbsClick
 */
export const handleCrumbsClick = ({
  setNestedSelection,
  groupBreadCrumbs,
  setGroupBreadCrumbs,
  crumb = null,
  isBackClick = false
}: BreadcrumbClickParams) => {
  const lastCrumbIndex    = groupBreadCrumbs?.length - 1;
  if (!isBackClick) {
    const currentCrumbIndex = groupBreadCrumbs.findIndex(x => x.title === crumb.title);
    if (currentCrumbIndex !== lastCrumbIndex) {
      const updates           = [...groupBreadCrumbs];
      updates.splice(currentCrumbIndex+1);
      setGroupBreadCrumbs(updates);
      setNestedSelection(crumb?.subAccounts);
    }
  } else {
    if (groupBreadCrumbs?.length === 1) {
      setGroupBreadCrumbs([]);
      setNestedSelection([]);
    } else {
      const prevCrumbIndex = lastCrumbIndex - 1;
      const prevCrumb      = groupBreadCrumbs[prevCrumbIndex];
      const updates        = [...groupBreadCrumbs];
      updates.splice(lastCrumbIndex);
      setGroupBreadCrumbs(updates);
      setNestedSelection(prevCrumb?.subAccounts);
    }
  }
};

/**
 *
 * @function handleChange
 */
export const handleChange = ({
  item,
  selectedVal,
  setSelectedVal,
  multiSelect
}: FiltersHandleChangeParms) => {
  const {value} = resolveFilter(item);
  const isSelected = !isUndefined(selectedVal.filter(selection =>
    resolveFilter(selection)?.value === value)[0]);
  if (isSelected) {
    if (multiSelect) {
      const updates = selectedVal.filter(selection =>
        resolveFilter(selection)?.value !== value);
      setSelectedVal(updates);
    } else {
      setSelectedVal([]);
    }
  } else {
    if (multiSelect) {
      const updates = [...selectedVal, item];
      setSelectedVal(updates);
    } else {
      setSelectedVal([item]);
    }
  }
};

/**
 *
 * @function resetFilter
 */
export const resetFilter = (
  filters: Array<object>,
  index: number = null,
  toggle: boolean = null
) => {
  let i = 0;
  const filter = {};
  while (i < filters?.length) {
    if (index && toggle && index === i) filter[i] = toggle;
    filter[i] = false;
    i++;
  }
  return filter;
};

/**
 *
 * @function onCancel
 */
export const onCancel = (
  toggleDetailView: Function,
  activeIndex: number
) => {
  toggleDetailView(activeIndex);
};

/**
 *
 * @function handleChipClickFromAppliedPanel
 */
export const handleChipClickFromAppliedPanel = (
  {selections, selected, onClick, id}, accountMap) => {
  if (id === AllFilterTypes.Groups) {
    const updates = [];
    const selectedAccountKey = getAccountKey(accountMap, selected);
    const updatedTree =
      updateCheckboxTreeState(selectedAccountKey, CheckboxState.Unchecked, accountMap);
    const selections = Object.keys(updatedTree).filter(key =>
      updatedTree[key]?.checked === 1);
    selections?.forEach(key => updates.push(updatedTree[key]?.title));
    onClick(updates, updatedTree);
  } else {
    const {value} = resolveFilter(selected);
    const updates = selections?.filter(selection =>
      resolveFilter(selection)?.value !== value);
    onClick(updates);
  }
};

/**
 *
 * @function handleClearCheckbox
 *
 */
export const handleClearCheckbox = (accountMap, saveGroupFilters) => {
  let updatedTree;
  if (accountMap) {
    const selectedAccountsKey = Object.keys(accountMap).filter(
      key => (accountMap[key]?.checked === CheckboxState.Checked ||
        accountMap[key]?.checked === CheckboxState.Indeterminate)
    );
    selectedAccountsKey?.forEach(key => {
      updatedTree = updateCheckboxTreeState(key,
        CheckboxState.Unchecked, updatedTree || accountMap);
    });
    // Reset Group filters and clear checkboxes when new saved filters are applied
    // or when all filters are cleared
    saveGroupFilters([], updatedTree);
  }
};

/**
 *
 * @function applyFilter
 *
 */
export const applyFilter = ({
  selectedElement,
  handleApply,
  clearAllFilters,
  accounts
}) => {
  const selectedFilters = {};
  for (const [name, value] of Object.entries(selectedElement?.initialFilters || {})) {
    const {key} = selectedElement?.filters?.filter(x => x.key === name)[0];
    const selectedItem = value?.selected;
    if (selectedItem?.length) {
      if (selectedFilters[key]?.selections) {
        selectedFilters[key].selections = [...selectedFilters[key]?.selections, ...selectedItem];
      } else {
        selectedFilters[key] = {
          selections : [...selectedItem]
        };
      }
    } else {
      clearAllFilters(resetGroupFilters(false, accounts), false);
    }
  }

  handleApply(selectedFilters, selectedElement);
};

/**
 *
 * @function removeSavedFilter
 *
 */
export function removeSavedFilter({
  element = null,
  savedFilterRecords,
  setSavedFilterRecords,
  currentFilters
}) {
  const pop = savedFilterRecords?.filter(x => x?.magicKey !==
    (element ? element.magicKey : currentFilters?.magicKey));
  const newFilter = [
    ...(pop || [])
  ];
  const newFilterWithoutDuplicate = removeDuplicate('magicKey', newFilter);
  setSavedFilterRecords(newFilterWithoutDuplicate);
  // Removes Saved Filters from user preferences
  UserPreferences.set(
    'tracking.filters',
    newFilterWithoutDuplicate,
    true
  );
}

/**
 *
 * @function handleEdit
 *
 */
export function handleEdit({
  currentFilters,
  initialFilters,
  setIsUpdatedInParent,
  setFilterBeingUpdated
}) {
  const updatedFilters = [];
  const originalFilters = [];
  const initialValues = Object.values(currentFilters?.initialFilters);
  const latestValues = Object.values(initialFilters);
  // Abstracts all the current filter option selections to latest values
  resolve(latestValues, updatedFilters);
  // Abstracts all the original filter option selections to initial values
  resolve(initialValues, originalFilters);
  // Checks if the Saved Filter is edited by comparing latest and initial values
  // Checks if the Saved Filter is edited by comparing latest and initial values
  if (isEqual(updatedFilters.sort(), originalFilters.sort())) {
    setIsUpdatedInParent(false);
  } else {
    setIsUpdatedInParent(true);
    setFilterBeingUpdated(currentFilters?.text);
  }
};

// Populates the passed array with selected filter values
const resolve = (values, filters) => {
  values.forEach(types => {
    types.selected?.forEach(element => {
      filters.push(element.id ? element.id :element);
    });
  });
};

/**
 *
 * @function getSelectedFiltersCount
 */
export function getSelectedFiltersCount(filters) {
  let selections = [];
  for (const x in filters) {
    selections = [...selections, ...filters[x]?.selected];
  }
  return selections.length;
}

/**
 *
 * @function getSelectedFilters
 */
export function getSelectedFilters(trackingFilters: TrackingFilters, processedFilters: ProcessedFilters) {
  const selectedFilters = [];
  for (const [name, value] of Object.entries(trackingFilters)) {
    const filterItem = processedFilters?.filter(
      (x) => x.key === name
    )[0];
    if (filterItem) {
      const {setSelectedFilter = null, key = null} = filterItem;
      const selectedItem = value?.selected;
      if (selectedItem?.length > 0) {
        selectedItem.forEach((item) => {
          const filterObj = {
            selected   : item,
            selections : [...selectedItem],
            onClick    : setSelectedFilter,
            id         : key
          };
          selectedFilters.push(filterObj);
        });
      }
    }
  }
  return selectedFilters;
}

/**
 *
 * @function displaySelectedFilters
 */
export function displaySelectedFilters({
  accountMap,
  trackingFilters,
  processedFilters,
  displayFilters
}: SelectedFiltersProps) {
  const selectedFilters = displayFilters ?? getSelectedFilters(trackingFilters, processedFilters);
  if (selectedFilters?.length > 0) {
    return selectedFilters.map((filter, index) => {
      const {value, label} = resolveFilter(filter.selected);
      return (
        <div key={`${value}_${index}`} style={{margin: '5px 0'}}>
          <Chip label={label}
            size='small'
            maxWidth={false}
            borderRadius='4px'
            labelColor={dlsColor('grey2')}
            hoverDefault
            showCustomDeleteIcon
            noTextTransform
            customDeleteIcon={<MaterialExitCloseIcon size={IconSize.small} />}
            onClick={function() {
              handleChipClickFromAppliedPanel(filter, accountMap);
            }}
            onDelete={function() {
              handleChipClickFromAppliedPanel(filter, accountMap);
            }} />
        </div>
      );
    });
  }
  return null;
};

/**
 * @displayFilterChildren
 *
 * Change filter stats based on count of filters.
 */
export function displayFilterChildren({
  selectedFilters,
  filterContainerRef,
  setIsFilterOverflow,
  setVisibleFilters,
  setMoreDropdownFilters,
  setMoreFilterPopupEl,
  extraPadding = 0
}: displayFilterChildrenParams) {
  let filterChildrenChipsWidth  = 0;
  let shownFiltersCount         = 0;
  const filterContainer         = filterContainerRef?.current;
  const {clientWidth, children} = filterContainer ?? {};
  const childrenLength          = children?.length;
  const filterCount             = selectedFilters?.length;

  for (let i = 0; i < childrenLength; i++) {
    filterChildrenChipsWidth += children[i].clientWidth ?? 0;
    if (clientWidth > (filterChildrenChipsWidth + extraPadding)) {
      shownFiltersCount = i + 1;
    }
  }

  const isFilterOverflow = (filterChildrenChipsWidth + extraPadding) > clientWidth;
  setIsFilterOverflow(isFilterOverflow);
  if (filterCount) {
    setVisibleFilters(selectedFilters.slice(0, shownFiltersCount));
    if (filterContainer && filterCount > shownFiltersCount) {
      setMoreDropdownFilters(selectedFilters.slice(shownFiltersCount));
    } else {
      setMoreFilterPopupEl(null);
      setMoreDropdownFilters(null);
    }
  }
}

/**
 * @function getTrackingSearchResults
 *
 * Returns value of tracking search results state.
 */
export const getTrackingSearchResults = (results) => {
  return results?.consolidated.slice(0) ?? [];
};

/**
 * Remove duplicate items from array and trim the item value.
 */
export const trimAndRemoveDuplicate = (items: any[]) => {
  return uniq(items?.map(item => !isObject(item) ? trim(item) : item));
};

/**
 * isFromGroupsAssetsFilter verify if the updated filter state is from
 * the groups/assets filter
 *
 * ```typescript
 * import {isFromGroupsAssetsFilter} from './helpers';
 *
 * if(isFromGroupsAssetsFilter(id)){
 *  doThis();
 * }else{
 *  doElse();
 * }
 *
 * ```
 *
 */
export const isFromGroupsAssetsFilter = (id: string): boolean => (
  id === Filters.groups ||
  id === Filters.assets ||
  id === Filters.devices
);

/**
 * getGroupsAssetsFilterUpdates returns updated filter state from
 * the groups/assets filter
 *
 * ```typescript
 * import {getGroupsAssetsFilterUpdates} from './helpers';
 *
 * ...
 *
 * const state = getGroupsAssetsFilterUpdates({
 *  filterState,
 *  selectedGroups,
 *  selectedAssets,
 *  selectedDevices
 * });
 * const createFilterState = {
 *  ...filterState,
 *  ...state
 * };
 *
 * ```
 *
 */
export const getGroupsAssetsFilterUpdates = ({
  filterState,
  selectedGroups,
  selectedAssets,
  selectedDevices
}) => ({
  groups  : {...filterState.groups, selected: selectedGroups?? filterState.groups.selected},
  assets  : {...filterState.assets, selected: selectedAssets?? filterState.assets.selected},
  devices : {...filterState.devices, selected: selectedDevices?? filterState.devices.selected}
});

/**
 * dateFilterMapping function ensures a proper mapping in between
 * user selected Date filter option and corresponding value to be displayed on UI.
 *
 *
 * ```typescript
 *
 *  import {dateFilterMapping} from '@/components/filter';
 *
 *  const getSelectedCount = useCallback((filterId: string): string | null => {
 *   if (filterId === Filters.date) {
 *     return dateFilterMapping(filterState[filterId]?.dateValue);
 *   };
 *  }, [filterState]);
 *
 * ```
 */
export const dateFilterMapping  = (filterId: string) => {
  switch (filterId) {
    case DateSelectorValues.Today:
      return translate('common:time.today');
    case DateSelectorValues.Yesterday:
      return translate('common:time.yesterday');
    case DateSelectorValues.LastSeven:
      return translate('common:time.lastDays', {number: 7});
    case DateSelectorValues.LastThirty:
      return translate('common:time.lastDays', {number: 30});
    case DateSelectorValues.Custom:
      return translate('common:time.custom');
    case DateSelectorValues.Live:
      return translate('common:time.live');
    default:
      return null;
  };
};

/**
 * Normalizes the selected items by ensuring every `id` is a string.
 * This prevents type inconsistencies when asset IDs may be returned
 * as either numbers or strings from different data sources.
 */
const normalizeSelectedIds = state => ({
  ...state,
  selected : state?.selected?.map(item => ({
    ...item,
    id : String(item.id)
  }))
});

// Helper to format filter state for backwards compatibility of saved filters
export const formatFilterState = (state: any, accountHierarchy?: any) => {
  const {period, startDate, endDate} = state?.date || {};
  const isLive = period === DateSelectorValues.Live;
  return {
    ...state,
    date : {
      period    : period || DateSelectorValues.Live,
      startDate : !isLive && startDate ? moment(startDate) : null,
      endDate   : !isLive && endDate ? moment(endDate) : null
    },
    assets : !state.assets ? {
      selected : []
    } : normalizeSelectedIds(state.assets),
    groups : {
      ...state.groups,
      selected : accountHierarchy ? getItemAllowed(state?.groups?.selected, accountHierarchy, 'id')
        : state?.groups?.selected
    }
  };
};

// Helper for getting tracking filters - only selections + uniquely structured date filter object
export const getFilterSelections = (filters: TrackingFilters) => {
  const selectedFilters = new Map();
  for (const [id, value] of Object.entries(filters)) {
    if (id === Filters.boundary) {
      selectedFilters.set(id, value?.[0]);
    } else if (id !== Filters.date) {
      selectedFilters.set(id, value.selected);
    } else {
      selectedFilters.set(id, value);
    }
  }
  return selectedFilters;
};
